import { langManager } from '../lang/langManager.js';
import { resetTurnstile } from '../utils/turnstile.js';
import { log } from '../utils/logging.js';
import {
  submitOrderForm,
  showFormStatus,
  validateSharedFields,
  SHARED_LIMITS
} from '../utils/formSubmit.js';

const LOG = (...args) => log('[BoutiqueCart]', ...args);
const WARN = (...args) => console.warn('[BoutiqueCart]', ...args);

const CART_STORAGE_KEY = 'bubar_boutique_cart';

/*********************************************
 * Cart State
 *********************************************/

/** @type {Map<string, {product: object, categoryId: string, quantity: number}>} */
const cart = new Map();

let CATEGORIES = {};

export function setCategories(categories) {
  CATEGORIES = categories || {};
  // Re-hydrate cart now that we have product data to resolve against
  loadCartFromStorage();
}

function t(value) {
  if (!value || typeof value === 'string') return value || '';
  const lang = langManager.getCurrentLanguage?.() || 'fr';
  return value[lang] || value['fr'] || value['en'] || Object.values(value)[0] || '';
}

/*********************************************
 * Storage persistence
 *********************************************/

/**
 * Persist cart to localStorage as a plain array of {categoryId, productId, quantity}.
 * We don't store the full product object — products.json is the source of truth.
 */
function saveCartToStorage() {
  try {
    const entries = [...cart.entries()].map(([key, item]) => ({
      categoryId: item.categoryId,
      productId: item.product.id,
      quantity: item.quantity
    }));
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(entries));
    LOG('saveCartToStorage:', entries.length, 'items');
  } catch (err) {
    WARN('saveCartToStorage failed:', err);
  }
}

/**
 * Restore cart from localStorage.
 * Must be called AFTER setCategories() so product objects can be resolved.
 */
function loadCartFromStorage() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return;

    let restored = 0;
    for (const { categoryId, productId, quantity } of entries) {
      const product = CATEGORIES[categoryId]?.products.find(p => p.id === productId);
      if (!product) {
        WARN('loadCartFromStorage: product not found, skipping', categoryId, productId);
        continue;
      }
      const key = `${categoryId}::${productId}`;
      cart.set(key, { product, categoryId, quantity });
      restored++;
    }
    LOG('loadCartFromStorage: restored', restored, 'items');
    if (restored > 0) renderCart();
  } catch (err) {
    WARN('loadCartFromStorage failed:', err);
  }
}

function clearCartStorage() {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (_) { }
}

/*********************************************
 * Cart operations
 *********************************************/

export function cartAdd(categoryId, productId) {
  const product = CATEGORIES[categoryId]?.products.find(p => p.id === productId);
  if (!product) {
    WARN('cartAdd: product not found', categoryId, productId);
    return;
  }
  const key = `${categoryId}::${productId}`;
  if (cart.has(key)) {
    cart.get(key).quantity += 1;
    LOG('cartAdd: incremented', key, cart.get(key).quantity);
  } else {
    cart.set(key, { product, categoryId, quantity: 1 });
    LOG('cartAdd: added', key);
  }
  saveCartToStorage();
  renderCart();
}

function cartRemove(key) {
  LOG('cartRemove', key);
  cart.delete(key);
  saveCartToStorage();
  renderCart();
}

function cartUpdateQty(key, delta) {
  if (!cart.has(key)) return;
  const item = cart.get(key);
  item.quantity += delta;
  LOG('cartUpdateQty', key, item.quantity);
  if (item.quantity <= 0) {
    cart.delete(key);
    LOG('cartUpdateQty: removed', key);
  }
  saveCartToStorage();
  renderCart();
}

function cartTotal() {
  let total = 0;
  for (const item of cart.values()) {
    total += item.product.price * item.quantity;
  }
  return total;
}

/*********************************************
 * Render
 *********************************************/

function renderCart() {
  LOG('renderCart: items =', cart.size, 'total =', cartTotal().toFixed(2));
  const container = document.getElementById('boutique-cart');
  if (!container) {
    WARN('renderCart: #boutique-cart not found');
    return;
  }

  const isEmpty = cart.size === 0;
  container.classList.toggle('cart--empty', isEmpty);

  const itemsEl = container.querySelector('.cart-items');
  const totalEl = container.querySelector('.cart-total-amount');
  const submitBtn = container.querySelector('#cart-submit-btn');

  if (!itemsEl) {
    WARN('renderCart: .cart-items not found inside #boutique-cart');
    return;
  }

  itemsEl.innerHTML = '';

  if (isEmpty) {
    // getText returns translated string for current lang — fallbacks per lang
    const emptyMsg =
      langManager.getText('cartEmpty') ||
      { fr: 'Votre panier est vide.', de: 'Ihr Warenkorb ist leer.', en: 'Your cart is empty.' }[
      langManager.getCurrentLanguage?.() || 'fr'
      ] ||
      'Votre panier est vide.';
    itemsEl.innerHTML = `<p class="cart-empty-msg">${emptyMsg}</p>`;
  } else {
    for (const [key, item] of cart.entries()) {
      const name = t(item.product.name);
      const unit = t(item.product.unit);
      const unitPrice = item.product.price.toFixed(2);
      const totalPrice = (item.product.price * item.quantity).toFixed(2);
      const row = document.createElement('div');
      row.className = 'cart-row';
      row.innerHTML = `
        <div class="cart-row__info">
          <span class="cart-row__name">${name}</span>
          <span class="cart-row__unit-price">${unitPrice} € / ${unit}</span>
        </div>
        <div class="cart-row__qty">
          <button class="qty-btn qty-btn--minus" data-key="${key}" aria-label="moins">−</button>
          <span class="qty-val">${item.quantity}</span>
          <button class="qty-btn qty-btn--plus" data-key="${key}" aria-label="plus">+</button>
        </div>
        <span class="cart-row__price">${totalPrice} €</span>
        <button class="cart-row__remove" data-key="${key}" aria-label="supprimer">🗑</button>
      `;
      itemsEl.appendChild(row);
    }
  }

  const badge = document.getElementById('boutique-cart-badge');
  if (badge) {
    const count = [...cart.values()].reduce((sum, i) => sum + i.quantity, 0);
    badge.textContent = count;
    badge.classList.toggle('visible', count > 0);
  }

  if (totalEl) {
    totalEl.textContent = cartTotal().toFixed(2) + ' €';
  }

  if (submitBtn) {
    submitBtn.disabled = isEmpty;
    if (!isEmpty) {
      const label = langManager.getText('boutiqueSendOrder') || 'Envoyer ma commande';
      submitBtn.textContent = `${label} — ${cartTotal().toFixed(2)} €`;
    } else {
      submitBtn.textContent = langManager.getText('boutiqueSendOrder') || 'Envoyer ma commande';
    }
  }
}

function bindCartEvents() {
  LOG('bindCartEvents');
  const container = document.getElementById('boutique-cart');
  if (!container) {
    WARN('bindCartEvents: #boutique-cart not found');
    return;
  }

  container.addEventListener('click', e => {
    const btn = e.target.closest('[data-key]');
    if (!btn) return;
    const key = btn.dataset.key;

    if (btn.classList.contains('qty-btn--minus')) {
      cartUpdateQty(key, -1);
    } else if (btn.classList.contains('qty-btn--plus')) {
      cartUpdateQty(key, 1);
    } else if (btn.classList.contains('cart-row__remove')) {
      cartRemove(key);
    }
  });

  const submitBtn = document.getElementById('cart-submit-btn');
  if (submitBtn) {
    submitBtn.addEventListener('click', handleCartSubmit);
    LOG('bindCartEvents: submit button bound');
  } else {
    WARN('bindCartEvents: #cart-submit-btn not found');
  }
}

/*********************************************
 * Market day date helpers
 *********************************************/

function nextWeekday(targetDay) {
  const now = new Date();
  const today = now.getDay();
  let diff = targetDay - today;
  if (diff <= 0) diff += 7;
  const result = new Date(now);
  result.setDate(now.getDate() + diff);
  return result;
}

function formatMarketDate(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/**
 * Inject the real upcoming Wednesday / Saturday dates into the pickup labels.
 * Also re-applies i18n translations for all data-i18n elements inside the cart,
 * since the cart HTML is injected dynamically and may be missed by langManager's
 * initial DOM scan.
 */
function updatePickupDates() {
  const nextWed = nextWeekday(3);
  const nextSat = nextWeekday(6);

  // Re-apply translations to the entire cart drawer first, so span.textContent
  // reflects the correct language before we append the date suffix.
  langManager.updatePageText();


  const wedLabel = document.querySelector('input[name="cart-pickup"][value="market-wed"]');
  const satLabel = document.querySelector('input[name="cart-pickup"][value="market-sat"]');

  if (wedLabel) {
    const span = wedLabel.closest('label')?.querySelector('span');
    if (span) {
      // After i18n re-apply, baseText is the translated string without the date suffix
      span.dataset.baseText = span.textContent.replace(/\s*\(.*\)$/, '').trim();
      span.textContent = `${span.dataset.baseText} (${formatMarketDate(nextWed)})`;
    }
  }
  if (satLabel) {
    const span = satLabel.closest('label')?.querySelector('span');
    if (span) {
      span.dataset.baseText = span.textContent.replace(/\s*\(.*\)$/, '').trim();
      span.textContent = `${span.dataset.baseText} (${formatMarketDate(nextSat)})`;
    }
  }

  LOG('updatePickupDates: Wed', formatMarketDate(nextWed), '/ Sat', formatMarketDate(nextSat));
}

/*********************************************
 * Cart icon + drawer open/close
 *********************************************/

// Use getter functions instead of captured references so we always hit
// the live DOM — the SPA may re-render the boutique partial on revisit,
// invalidating stale element references captured at initCartIcon() time.
const getDrawer = () => document.getElementById('boutique-cart');
const getOverlay = () => document.getElementById('boutique-cart-overlay');
const getIcon = () => document.getElementById('boutique-cart-icon');

function openCartDrawer() {
  LOG('cart open triggered');
  getDrawer()?.classList.add('open');
  getOverlay()?.classList.add('open');

  const icon = getIcon();
  if (icon) {
    icon.style.opacity = '0';
    icon.style.pointerEvents = 'none';
  }

  // Re-render Turnstile when drawer opens.
  // The drawer starts off-screen via transform:translateX(100%), which
  // creates a stacking context that breaks Turnstile's position:fixed iframe.
  // Rendering only when open (drawer is at translateX(0)) avoids this.
  if (window.turnstile) {
    const container = document.getElementById('boutique-turnstile-container');
    if (container && !container.querySelector('iframe')) {
      window.turnstile.render('#boutique-turnstile-container', {
        sitekey: '0x4AAAAAACb2ChGka9Qkz6cV',
        theme: 'light',
        callback: 'onBoutiqueTurnstileSuccess'
      });
    }
  }
}

function closeCartDrawer() {
  LOG('cart close triggered');
  getDrawer()?.classList.remove('open');
  getOverlay()?.classList.remove('open');

  const icon = getIcon();
  if (icon) {
    icon.style.opacity = '';
    icon.style.pointerEvents = '';
  }
}

// ---- initCartIcon: guarded so nav listeners are only added once ----

let iconNavBound = false;

function initCartIcon() {
  LOG('initCartIcon');

  const iconEl = getIcon();
  const drawerEl = getDrawer();
  const closeBtn = drawerEl?.querySelector('.cart-drawer-close');

  // Always (re-)bind click handlers — elements may be re-created on SPA revisit
  iconEl?.addEventListener('click', openCartDrawer);
  iconEl?.addEventListener('keydown', e => e.key === 'Enter' && openCartDrawer());
  closeBtn?.addEventListener('click', closeCartDrawer);
  getOverlay()?.addEventListener('click', closeCartDrawer);

  // Show icon — we are on boutique right now
  getIcon()?.classList.add('cart-icon--visible');

  // Navigation visibility — bind once only; handler reads live DOM via getIcon()
  if (!iconNavBound) {
    iconNavBound = true;

    window.addEventListener('navigate-home', () =>
      getIcon()?.classList.remove('cart-icon--visible'));

    window.addEventListener('popstate', () =>
      getIcon()?.classList.remove('cart-icon--visible'));

    window.addEventListener('navigate-scene', e =>
      getIcon()?.classList.toggle(
        'cart-icon--visible',
        e.detail?.sceneName === 'boutique'
      ));

    window.addEventListener('navigate-section', e =>
      getIcon()?.classList.toggle(
        'cart-icon--visible',
        e.detail?.section === 'boutique'
      ));
  }
}



let navCleanupBound = false;

function bindNavigationCleanup() {
  if (navCleanupBound) return;
  navCleanupBound = true;

  const onNavigateSection = e => {
    if (e.detail?.section !== 'boutique') {
      LOG('navigate-section away from boutique — closing cart drawer');
      closeCartDrawer();
    }
  };
  const onNavigateScene = e => {
    if (e.detail?.sceneName && e.detail.sceneName !== 'boutique') {
      LOG('navigate-scene away from boutique — closing cart drawer');
      closeCartDrawer();
    }
  };

  window.addEventListener('navigate-home', closeCartDrawer);
  window.addEventListener('navigate-section', onNavigateSection);
  window.addEventListener('navigate-scene', onNavigateScene);
  window.addEventListener('popstate', closeCartDrawer);
  window.addEventListener('pagehide', closeCartDrawer);
}

/*********************************************
 * Cart Email Submission
 *********************************************/

async function handleCartSubmit() {
  LOG('handleCartSubmit called, cart size =', cart.size);

  const submitBtn = document.getElementById('cart-submit-btn');

  const name = document.getElementById('cart-name')?.value.trim() || '';
  const email = document.getElementById('cart-email')?.value.trim() || '';
  const phone = document.getElementById('cart-phone')?.value.trim() || '';
  const pickup = document.querySelector('input[name="cart-pickup"]:checked')?.value;
  const message = document.getElementById('cart-message')?.value.trim() || '';

  // Use the boutique-prefixed token field (not the generic 'turnstile_token')
  const token = document.getElementById('boutique-turnstile-token')?.value || '';

  const statusId = 'cart-status';

  if (cart.size === 0) {
    showFormStatus({
      submitBtn,
      statusId,
      message: langManager.getText('cartEmpty') || 'Votre panier est vide.',
      isError: true
    });
    return;
  }

  const sharedError = validateSharedFields({ name, email, phone, message }, langManager.getText);
  if (sharedError) {
    showFormStatus({ submitBtn, statusId, message: sharedError, isError: true });
    return;
  }

  if (!pickup) {
    showFormStatus({
      submitBtn,
      statusId,
      message:
        langManager.getText('boutiquePickupRequired') || 'Veuillez choisir un mode de retrait.',
      isError: true
    });
    return;
  }

  // Validate Turnstile — use boutique-prefixed container id
  const turnstileContainer = document.getElementById('boutique-turnstile-container');
  const hasTurnstile = !!turnstileContainer;
  if (hasTurnstile && !token) {
    showFormStatus({
      submitBtn,
      statusId,
      message: 'Veuillez compléter le captcha de sécurité.',
      isError: true
    });
    return;
  }

  const orderLines = [];
  for (const item of cart.values()) {
    orderLines.push({
      name: t(item.product.name),
      quantity: item.quantity,
      unit: item.product.unit,
      priceUnit: item.product.price,
      priceTotal: +(item.product.price * item.quantity).toFixed(2),
      category: t(CATEGORIES[item.categoryId]?.name) || item.categoryId
    });
  }
  const orderTotal = cartTotal().toFixed(2);

  LOG('handleCartSubmit: order', orderLines, 'total', orderTotal);

  const totalLabel = `${langManager.getText('boutiqueSendOrder') || 'Envoyer ma commande'} — ${cartTotal().toFixed(2)} €`;


  await submitOrderForm({
    submitBtn,
    statusId,
    sendingLabel: 'Envoi en cours…',
    idleLabel: totalLabel,
    payload: {
      name,
      phone,
      email,
      message,
      pickup,
      token,
      lang: langManager.getCurrentLanguage?.() || 'de',
      booking: {
        type: 'boutique-order',
        lines: orderLines,
        total: orderTotal
      }
    },
    successMessage: result =>
      '✅ ' + (result.message || 'Commande envoyée ! Nous vous contacterons bientôt.'),
    resetButton: {
      id: 'cart-reset-btn',
      label: langManager.getText('boutiqueNewOrder') || 'Nouvelle commande'
    },
    onSuccess: () => {
      resetTurnstile('boutique-turnstile-container');
      const tokenField = document.getElementById('boutique-turnstile-token');
      if (tokenField) tokenField.value = '';

      cart.clear();
      clearCartStorage();
      renderCart();

      ['cart-name', 'cart-email', 'cart-phone', 'cart-message'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
      document.querySelectorAll('input[name="cart-pickup"]').forEach(r => {
        r.checked = false;
      });
    }
  });
}

/*********************************************
 * Public init
 *********************************************/

export function initCart() {
  LOG('initCart');
  renderCart();
  bindCartEvents();
  initCartIcon();
  updatePickupDates();
  bindNavigationCleanup();

  // Re-apply i18n + dates when language changes
  // AFTER
  const onLangChange = () => { renderCart(); updatePickupDates(); };
  window.addEventListener('languageChanged', onLangChange);
}